import React, { useRef, useEffect, useState } from 'react';
import { TextField, TextFieldProps, CircularProgress, InputAdornment } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';

// Address components interface
export interface ParsedAddress {
  address_line1: string;
  address_line2: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  formatted_address: string;
}

interface AddressAutocompleteProps extends Omit<TextFieldProps, 'onChange'> {
  onAddressSelect: (address: ParsedAddress) => void;
  value?: string;
  onChange?: (value: string) => void;
  countryRestriction?: string; // ISO 3166-1 Alpha-2 country code
  placeholder?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  value = '',
  onChange,
  countryRestriction = 'au', // Default to Australia
  placeholder = 'Start typing your address...',
  ...textFieldProps
}) => {
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);

  // Check if Google Maps is loaded
  const isGoogleMapsLoaded = typeof window !== 'undefined' &&
    typeof window.google !== 'undefined' &&
    typeof window.google.maps !== 'undefined' &&
    typeof window.google.maps.places !== 'undefined';

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    console.log('[AddressAutocomplete] Google Maps loaded:', isGoogleMapsLoaded);
  }, [isGoogleMapsLoaded]);

  const parseAddressComponents = (place: google.maps.places.PlaceResult): ParsedAddress => {
    const components = place.address_components || [];

    let streetNumber = '';
    let route = '';
    let subpremise = ''; // Unit/Apartment number
    let suburb = '';
    let state = '';
    let postcode = '';
    let country = '';

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('subpremise')) {
        subpremise = component.long_name;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        suburb = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        state = component.short_name; // e.g., NSW, VIC
      }
      if (types.includes('postal_code')) {
        postcode = component.long_name;
      }
      if (types.includes('country')) {
        country = component.short_name;
      }
    });

    // Build address_line1 from street number and route
    const address_line1 = [streetNumber, route].filter(Boolean).join(' ');

    // Put subpremise (unit/apartment) in address_line2 if it exists
    const address_line2 = subpremise ? `Unit ${subpremise}` : '';

    return {
      address_line1,
      address_line2,
      suburb,
      state,
      postcode,
      country,
      formatted_address: place.formatted_address || ''
    };
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      setIsLoading(true);
      const place = autocompleteRef.current.getPlace();

      if (place && place.address_components) {
        const parsedAddress = parseAddressComponents(place);
        onAddressSelect(parsedAddress);
        setInputValue(parsedAddress.formatted_address);
        onChange?.(parsedAddress.formatted_address);
      }
      setIsLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
    console.log('[AddressAutocomplete] Autocomplete loaded successfully');
  };

  // If Google Maps is not loaded, render a regular TextField
  if (!isGoogleMapsLoaded) {
    return (
      <TextField
        {...textFieldProps}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        InputProps={{
          ...textFieldProps.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <LocationIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return (
    <Autocomplete
      onLoad={handleLoad}
      onPlaceChanged={handlePlaceChanged}
      options={{
        componentRestrictions: { country: countryRestriction },
        fields: ['address_components', 'formatted_address'],
        types: ['address']
      }}
    >
      <TextField
        {...textFieldProps}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        InputProps={{
          ...textFieldProps.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <LocationIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : textFieldProps.InputProps?.endAdornment
        }}
      />
    </Autocomplete>
  );
};

export default AddressAutocomplete;
